// Morm Signer WASM Integration
// This module provides a JavaScript interface to the morm_signer WASM module

import { initWasm } from "./wasmLoader";
import {
  AddressResult,
  SignResult,
  SignOptions,
  DepositResult,
  MormSignerStatus,
  AgentDelegationResult,
  AgentDelegationOptions,
  KeypairResult,
  MnemonicResult,
  GenerateSingleSignatureResult,
  GenerateMultisignResult,
  SupportedSignatureTypesResult,
} from "../types";

class MormSigner {
  private isInitialized: boolean = false;
  private wasmLoaded: boolean = false;

  // Initialize the WASM module
  async initialize(): Promise<boolean> {
    try {
      this.wasmLoaded = await initWasm();
      this.isInitialized = this.wasmLoaded;
      return this.wasmLoaded;
    } catch (error: any) {
      console.error("Failed to initialize MormSigner:", error);
      // Re-throw the error so it can be caught and displayed in the UI
      throw new Error(error?.message || "Failed to initialize WASM module");
    }
  }

  // Check if the signer is ready
  isReady(): boolean {
    return (
      this.isInitialized &&
      this.wasmLoaded &&
      typeof window.mormGenerateEthereumKeypair === "function" &&
      typeof window.GenerateSingleSignature === "function" &&
      typeof window.GenerateMultisignSignatures === "function" &&
      typeof window.GetSupportedSignatureTypes === "function" &&
      typeof window.IsSignatureTypeSupported === "function"
    );
  }

  // Generate address from mnemonic
  async generateAddress(
    mnemonic: string,
    addressIndex: number = 0,
  ): Promise<AddressResult> {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      const result = window.deriveAddress(mnemonic, addressIndex);

      if (typeof result === "string") {
        return {
          success: true,
          address: result,
          index: addressIndex,
          mnemonic: mnemonic.substring(0, 20) + "...",
        };
      } else if (result && (result as any).error) {
        return {
          success: false,
          error: (result as any).error,
        };
      } else {
        return {
          success: false,
          error: "Failed to derive address",
        };
      }
    } catch (error) {
      console.error("Failed to generate address:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Generate mnemonic
  async generateMnemonic(length: number = 12): Promise<MnemonicResult> {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      const result = window.generateMnemonic(length);

      if (typeof result === "string") {
        return {
          success: true,
          mnemonic: result,
        };
      } else if (result && (result as any).error) {
        return {
          success: false,
          error: (result as any).error,
        };
      } else {
        return {
          success: false,
          error: "Failed to generate mnemonic",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Generate Ethereum keypair
  async generateKeypair(): Promise<KeypairResult> {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      const result = window.mormGenerateEthereumKeypair();

      if (result.error) {
        return {
          success: false,
          error: result.error,
        };
      }

      return {
        success: true,
        privateKey: result.privateKey,
        address: result.address,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Sign a message
  async signMessage(
    mnemonic: string,
    message: string,
    options: SignOptions = {},
  ): Promise<SignResult> {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      // Determine algorithm
      const algorithm = options.useEIP712 ? "ECDSA_LEGACY" : "ECDSA_LEGACY";

      const result = window.generateSignature(algorithm, mnemonic, message);

      if (typeof result === "string") {
        return {
          success: true,
          signature: result,
          message: message,
          options: options,
        };
      } else if (result && (result as any).error) {
        return {
          success: false,
          error: (result as any).error,
        };
      } else {
        return {
          success: false,
          error: "Failed to generate signature",
        };
      }
    } catch (error) {
      console.error("Failed to sign message:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Sign a deposit message
  async signDeposit(
    mnemonic: string,
    walletAddress: string,
    amount: string,
    options: SignOptions = {},
  ): Promise<DepositResult> {
    const depositOptions: SignOptions = {
      ...options,
      depositMode: true,
      walletAddress: walletAddress,
      amount: amount,
    };

    // Create deposit message JSON
    const message = JSON.stringify({
      action: "deposit",
      owner: walletAddress,
      amount: amount,
      nonce: options.nonce || Date.now(),
      deadline: options.deadline || Date.now() + 3600000,
    });

    const result = await this.signMessage(mnemonic, message, depositOptions);
    return {
      success: result.success,
      signature: result.signature,
      message: result.message,
      address: walletAddress,
      options: result.options,
      error: result.error,
    };
  }

  // Sign an EIP-712 message
  async signEIP712(
    mnemonic: string,
    message: string,
    options: SignOptions = {},
  ): Promise<SignResult> {
    const eip712Options: SignOptions = {
      ...options,
      useEIP712: true,
    };

    return this.signMessage(mnemonic, message, eip712Options);
  }

  // Prepare agent sign request (onboard/approve agent)
  async prepareAgentSign(
    options: AgentDelegationOptions,
  ): Promise<AgentDelegationResult> {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      const typedData = window.mormPrepareAgentSign(
        options.owner,
        options.agent,
        options.expiresInDays,
        options.permissions,
        options.nonce,
      );

      if (typedData && typedData.error) {
        return {
          success: false,
          error: typedData.error,
        };
      }

      return {
        success: true,
        typedData: typedData,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Sign agent approval (connect/approve agent action)
  async signAgentApproval(
    mnemonic: string,
    options: AgentDelegationOptions,
  ): Promise<AgentDelegationResult> {
    // First prepare the typed data
    const prepareResult = await this.prepareAgentSign(options);
    if (!prepareResult.success || !prepareResult.typedData) {
      return prepareResult;
    }

    // Create message for signing
    const message = JSON.stringify({
      action: "agent::approve",
      wallet: options.owner,
      agent: options.agent,
      agentName: options.agentName || "",
      expiresInDays: options.expiresInDays,
      permissions: options.permissions,
      nonce: options.nonce,
      deadline: Date.now() + options.expiresInDays * 24 * 60 * 60 * 1000,
    });

    // Sign the message
    const signResult = await this.signMessage(mnemonic, message, {
      useEIP712: true,
    });

    if (!signResult.success) {
      return {
        success: false,
        error: signResult.error,
      };
    }

    return {
      success: true,
      typedData: prepareResult.typedData,
      signature: signResult.signature,
    };
  }

  // Verify signature
  async verifySignature(
    algorithm: string,
    message: string,
    signature: string,
  ): Promise<boolean> {
    if (!this.isReady()) {
      throw new Error("MormSigner not initialized");
    }

    try {
      const result = window.verifySignature(algorithm, message, signature);
      return typeof result === "boolean" ? result : false;
    } catch (error) {
      console.error("Failed to verify signature:", error);
      return false;
    }
  }

  // Map algorithm from payload type
  mapAlgorithm(payloadType: string): string {
    if (!this.isReady()) {
      return "ECDSA_LEGACY";
    }

    try {
      return window.mapAlgorithm(payloadType);
    } catch (error) {
      console.error("Failed to map algorithm:", error);
      return "ECDSA_LEGACY";
    }
  }

  // Validate nonce
  validateNonce(owner: string, nonce: string): boolean {
    if (!this.isReady()) {
      return false;
    }

    try {
      return window.validateNonce(owner, nonce);
    } catch (error) {
      console.error("Failed to validate nonce:", error);
      return false;
    }
  }

  // Get status information
  getStatus(): MormSignerStatus {
    return {
      initialized: this.isInitialized,
      wasmLoaded: this.wasmLoaded,
      ready: this.isReady(),
    };
  }

  // Generate a single signature using the new Universal Signature Generator API
  // This is a direct interface that accepts EIP712Tx JSON, private key, and address
  async generateSingleSignature(
    txJSON: string,
    privateKeyHex: string,
    signerAddressHex: string,
  ): Promise<GenerateSingleSignatureResult> {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      const result = window.GenerateSingleSignature(
        txJSON,
        privateKeyHex,
        signerAddressHex,
      );

      if (result.error) {
        return {
          success: false,
          error: result.error,
        };
      }

      return {
        success: true,
        signature: result.signature,
      };
    } catch (error) {
      console.error("Failed to generate single signature:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Generate multiple signatures for multisig transactions
  // This accepts an array of private keys and can use signature aggregation
  async generateMultisignSignatures(
    txJSON: string,
    privateKeysHex: string[],
    useAggregation: boolean = false,
  ): Promise<GenerateMultisignResult> {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      const result = window.GenerateMultisignSignatures(
        txJSON,
        privateKeysHex,
        useAggregation,
      );

      if (result.error) {
        return {
          success: false,
          error: result.error,
        };
      }

      // Parse the updated transaction
      let parsedTx: any = null;
      if (result.tx) {
        try {
          parsedTx = JSON.parse(result.tx);
        } catch (parseError) {
          console.warn("Failed to parse updated transaction JSON:", parseError);
        }
      }

      return {
        success: true,
        tx: parsedTx,
        txJSON: result.tx,
      };
    } catch (error) {
      console.error("Failed to generate multisign signatures:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Get the list of supported signature types
  // Returns an array of signature type strings (e.g., ['ECDSA_LEGACY', 'SCHNORR_AGGREGATE', ...])
  getSupportedSignatureTypes(): SupportedSignatureTypesResult {
    if (!this.isReady()) {
      return {
        success: false,
        error: "MormSigner not initialized",
      };
    }

    try {
      const types = window.GetSupportedSignatureTypes();
      return {
        success: true,
        types: types,
      };
    } catch (error) {
      console.error("Failed to get supported signature types:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Check if a specific signature type is supported
  // Useful for validating user input before attempting to sign
  isSignatureTypeSupported(sigType: string): boolean {
    if (!this.isReady()) {
      return false;
    }

    try {
      return window.IsSignatureTypeSupported(sigType);
    } catch (error) {
      console.error("Failed to check signature type support:", error);
      return false;
    }
  }
}

// Create and export a singleton instance
const mormSigner = new MormSigner();
export default mormSigner;
